import { Sparkles, Wand2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useLocale } from "@/lib/i18n/locale-context";

export type AiInsight = {
  /** Kort kommentar på svenska om bilden. */
  comment?: string;
  /** Vad AI:n valde att sälja ur bilden. */
  items?: string[];
  /** Varför just det valet ger bäst försäljning. */
  reason?: string;
  /** Andra saker i bilden som säljaren kan välja i stället. */
  alternatives?: string[];
  /** Tips, t.ex. att ta en egen bild per pryl. */
  tip?: string;
  /** Sant när en person måste bort ur bilden först. */
  needsPersonRemoval?: boolean;
};

export function AiInsightCard({
  insight,
  onPick,
  onCleanImage,
  busy,
}: {
  insight: AiInsight;
  /** Skriv om annonsen på ett annat föremål ur bilden. */
  onPick?: (item: string) => void;
  /** Öppna bildstudion och rensa bort personen/bakgrunden. */
  onCleanImage?: () => void;
  busy?: boolean;
}) {
  const { lang } = useLocale();
  const t = lang === "es" ? { written: "El anuncio está redactado para", person: "Se ve una persona en la imagen. Debes eliminarla antes de publicar; podemos limpiar la imagen por ti.", clean: "Limpiar imagen", other: "¿Quieres vender otro objeto de la imagen?" }
    : lang === "en" ? { written: "The listing is written for", person: "A person is visible in the image. They must be removed before publishing; we can clean the image for you.", clean: "Clean image", other: "Would you like to sell another item in the image?" }
      : { written: "Annonsen är skriven på", person: "Det syns en person på bilden. Personen behöver bort innan du publicerar – vi kan rensa bilden åt dig.", clean: "Rensa bilden", other: "Vill du sälja något annat på bilden?" };
  const items = insight.items?.filter(Boolean) ?? [];
  const alternatives = (insight.alternatives ?? []).filter(
    (alt) => alt && !items.some((item) => item.toLowerCase() === alt.toLowerCase()),
  );

  if (!insight.comment && !items.length && !alternatives.length) return null;

  return (
    <div className="rounded-lg border border-ocean-300/30 bg-ocean-300/5 p-4">
      <div className="flex items-start gap-2.5">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-ocean-300" />
        <div className="min-w-0 space-y-2">
          {insight.comment && (
            <p className="text-sm leading-relaxed text-foreground">{insight.comment}</p>
          )}
          {items.length > 0 && (
            <p className="text-sm text-muted-foreground">
               {t.written}{" "}
              <span className="font-semibold text-foreground">{items.join(" + ")}</span>
              {insight.reason ? ` – ${insight.reason}` : "."}
            </p>
          )}
          {insight.needsPersonRemoval && onCleanImage && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
              <p className="text-sm text-foreground">
                 {t.person}
              </p>
              <Button
                type="button"
                size="sm"
                className="mt-2"
                disabled={busy}
                onClick={onCleanImage}
              >
                 <Wand2 className="mr-1.5 h-4 w-4" /> {t.clean}
              </Button>
            </div>
          )}
          {alternatives.length > 0 && onPick && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                 {t.other}
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {alternatives.map((alt) => (
                  <button
                    key={alt}
                    type="button"
                    disabled={busy}
                    onClick={() => onPick(alt)}
                    className="rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-foreground transition-colors hover:border-primary/50 hover:text-primary disabled:opacity-50"
                  >
                    {alt}
                  </button>
                ))}
              </div>
            </div>
          )}
          {insight.tip && <p className="text-xs text-muted-foreground">{insight.tip}</p>}
        </div>
      </div>
    </div>
  );
}
